import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogProps,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import LinearProgress from '@mui/material/LinearProgress';
import { DatePicker } from '@mui/x-date-pickers';
import { format, parse } from 'date-fns';
import * as React from 'react';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { BasicDialog } from '../common/BasicDialog';
import { Accident, RegistryCodeNote, RegistryIdName } from '../types/accident';
import { AccountingDocument, AccountingDocumentsInvoice, CashReceipt } from './type/accountingDocuments';

export const mapAccountingToCashReceipt = (doc: AccountingDocument): CashReceipt => {
  return {
    document: {
      centreId: doc.centreId,
      documentCode: doc.documentCode ?? '',
      paymentMethod: doc.paymentMethod?.type ?? 'cash',
      issueDate: parse(doc.issueDate, 'dd.MM.yyyy', new Date()),
      issueDays: doc.issueDays ?? 14,
      taxEnabled: !!doc.taxEnabled,
      accidentId: doc.accidentId ?? 0,
      note: doc.note ?? '',
      isPaid: !!doc.isPaid,
      isPaidDate: doc.isPaidDate ? parse(doc.isPaidDate, 'dd.MM.yyyy', new Date()) : undefined,
      isPaidAmount:
        typeof doc.isPaidAmount === 'string' ? Number.parseFloat(doc.isPaidAmount) : (doc.isPaidAmount as any) ?? 0,
      internalNote: doc.internalNote ?? '',
      creditedInvoiceId: doc.creditedInvoiceId ?? undefined
    },
    items: (doc.items ?? []).map((it) => ({
      quantity: it.quantity ?? 0,
      unit: it.unit ?? '',
      description: it.description ?? '',
      tax: it.tax ?? 21,
      unitPrice: it.unitPrice ?? 0
    }))
  };
};

interface NewCashReceiptDialogProps extends Omit<DialogProps, 'onSubmit'> {
  defaultData?: CashReceipt;
  onSubmit?: (data: CashReceipt) => void;
  onClose?: () => void;
  accidents?: Accident[];
  accidentId?: number;
  accountingCentres?: RegistryIdName[];
  documentCodes?: RegistryCodeNote[];
  isLoading?: boolean;
  isLoadingAccidents?: boolean;
  invoices?: AccountingDocumentsInvoice[];
  invoicesLoading?: boolean;
}

export const NewCashReceiptDialog: React.FC<NewCashReceiptDialogProps> = ({
  open,
  onClose,
  onSubmit,
  defaultData,
  accidents,
  accidentId,
  accountingCentres,
  documentCodes,
  isLoading,
  isLoadingAccidents,
  invoices,
  invoicesLoading,
  ...props
}) => {
  const methods = useForm<CashReceipt>({
    shouldUnregister: false,
    defaultValues: {
      document: {
        centreId: 1,
        documentCode: '',
        paymentMethod: 'cash',
        issueDate: new Date(),
        issueDays: 14,
        taxEnabled: false,
        accidentId: accidentId ?? 0,
        note: '',
        isPaid: true,
        isPaidDate: new Date(),
        isPaidAmount: 0,
        internalNote: '',
        creditedInvoiceId: undefined
      },
      items: [
        {
          quantity: 1,
          unit: 'ks',
          description: '',
          tax: 21,
          unitPrice: 0
        }
      ]
    }
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const isPaid = watch('document.isPaid');
  const taxEnabled = watch('document.taxEnabled');

  const calculateTotalAmount = () => {
    const watchedItems = watch('items');
    return watchedItems.reduce((sum, item) => {
      const itemTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
      return sum + itemTotal;
    }, 0);
  };

  const updateTotalAmount = () => {
    if (isPaid) {
      methods.setValue('document.isPaidAmount', calculateTotalAmount(), {
        shouldValidate: true
      });
    }
  };

  const addItem = () => append({ quantity: 1, unit: '', description: '', tax: 21, unitPrice: 0 });

  useEffect(() => {
    if (open && defaultData) {
      const formData = {
        ...defaultData,
        items: defaultData.items ?? []
      };
      reset(formData);
    } else if (open && !defaultData) {
      reset({
        document: {
          centreId: 1,
          documentCode: '',
          paymentMethod: 'cash',
          issueDate: new Date(),
          issueDays: 14,
          taxEnabled: false,
          accidentId: accidentId ?? 0,
          note: '',
          isPaid: true,
          isPaidDate: new Date(),
          isPaidAmount: 0,
          internalNote: '',
          creditedInvoiceId: undefined
        },
        items: [
          {
            quantity: 1,
            unit: 'ks',
            description: '',
            tax: 21,
            unitPrice: 0
          }
        ]
      });
    }
  }, [open, defaultData, reset]);

  return (
    <BasicDialog
      open={open}
      onClose={(_, __) => onClose?.()}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: '100%',
          minWidth: { xs: '100%', sm: 600 }
        }
      }}
      title={defaultData ? 'Upravit pokladní doklad' : 'Nový pokladní doklad'}
      {...props}
    >
      <form
        onSubmit={handleSubmit((data) => {
          const parsedData: CashReceipt = {
            ...data,
            document: {
              ...data.document,
              issueDate: format(data.document.issueDate, 'dd.MM.yyyy'),
              isPaidDate:
                data.document.isPaid && data.document.isPaidDate
                  ? format(data.document.isPaidDate, 'dd.MM.yyyy')
                  : ('' as any),
              isPaidAmount: data.document.isPaid ? data.document.isPaidAmount : 0
            }
          };
          onSubmit?.(parsedData);
        })}
        style={{ padding: 3, paddingBottom: 16 }}
      >
        <DialogContent sx={{ pt: 2 }}>
          {isLoadingAccidents && <LinearProgress sx={{ mb: 2 }} />}
          <Grid container spacing={2} pt={3}>
            {!accidentId && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="document.accidentId"
                  rules={{ required: 'Vyberte nehodu' }}
                  render={({ field }) => {
                    const loading = isLoadingAccidents || accidents === undefined;
                    const options = accidents ?? [];
                    const ghost =
                      field.value != null && !options.find((o) => o.id === field.value)
                        ? ({
                            id: field.value,
                            contractNumber: 'Vyberte nehodu…',
                            user: {},
                            car: {}
                          } as any)
                        : null;

                    const valueOption = options.find((o) => o.id === field.value) ?? ghost;

                    return (
                      <Autocomplete
                        options={options}
                        value={valueOption}
                        loading={loading}
                        onChange={(_, option) => field.onChange(option ? (option as any).id : null)}
                        getOptionLabel={(o: any) =>
                          ghost && o === ghost
                            ? 'Vyberte nehodu…'
                            : [
                                [o.contractNumber, o.user?.company?.companyName, o.user?.name, o.user?.surname]
                                  .filter(Boolean)
                                  .join(' '),
                                [o.car?.brand, o.car?.model, o.car?.plateNumber].filter(Boolean).join(' ')
                              ]
                                .filter(Boolean)
                                .join(', ')
                        }
                        isOptionEqualToValue={(o, v) => o.id === (v as any).id}
                        disabled={options.length === 0 && !ghost}
                        loadingText="Načítám nehody…"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Nehoda"
                            fullWidth
                            error={!!errors.document?.accidentId}
                            helperText={
                              errors.document?.accidentId?.message ||
                              (loading && !ghost
                                ? 'Načítám nehody…'
                                : options.length === 0 && !ghost
                                  ? 'Žádné nehody nejsou k dispozici'
                                  : undefined)
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loading ? <CircularProgress size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.creditedInvoiceId"
                render={({ field }) => {
                  const loading = invoicesLoading;
                  const options = invoices;

                  const ghost =
                    field.value != null && !options?.find((o) => o.id === field.value)
                      ? ({
                          id: field.value,
                          documentNumber: field.value,
                          accidentNumber: undefined
                        } as AccountingDocumentsInvoice)
                      : null;

                  const valueOption = options?.find((o) => o.id === field.value) ?? ghost;

                  return (
                    <Autocomplete
                      options={options ?? []}
                      value={valueOption}
                      loading={loading}
                      onChange={(_, option) =>
                        field.onChange(option ? (option as AccountingDocumentsInvoice).id : undefined)
                      }
                      getOptionLabel={(o: AccountingDocumentsInvoice) => {
                        const base = String(o.documentNumber);
                        return o.accidentNumber ? `${base} — nehoda ${o.accidentNumber}` : base;
                      }}
                      isOptionEqualToValue={(o, v) => o.id === (v as AccountingDocumentsInvoice).id}
                      disabled={options?.length === 0 && !ghost}
                      loadingText="Načítám faktury…"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Úhrada faktury"
                          fullWidth
                          placeholder="Vyberte fakturu k úhradě (volitelné)"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          helperText={
                            loading && !ghost
                              ? 'Načítám faktury…'
                              : options?.length === 0 && !ghost
                                ? 'Žádné faktury nejsou k dispozici'
                                : 'Pokud jde o úhradu konkrétní faktury, vyberte ji zde'
                          }
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.centreId"
                rules={{ required: 'Vyberte středisko' }}
                render={({ field }) => {
                  const isLoading = accountingCentres === undefined;
                  const isEmpty = Array.isArray(accountingCentres) && accountingCentres.length === 0;

                  return (
                    <TextField
                      {...field}
                      select
                      label="Středisko"
                      fullWidth
                      disabled={isLoading || isEmpty}
                      error={!!errors.document?.centreId}
                      helperText={
                        errors.document?.centreId?.message ||
                        (isLoading ? 'Načítám střediska…' : isEmpty ? 'Žádná střediska nejsou k dispozici' : undefined)
                      }
                      value={field.value ?? ''}
                    >
                      {isLoading && <MenuItem disabled>Načítám…</MenuItem>}
                      {!isLoading && isEmpty && <MenuItem disabled>Žádné možnosti</MenuItem>}
                      {!isLoading &&
                        !isEmpty &&
                        accountingCentres!.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  );
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.documentCode"
                rules={{ required: 'Vyberte kód dokladu' }}
                render={({ field }) => {
                  const isLoading = documentCodes === undefined;
                  const isEmpty = Array.isArray(documentCodes) && documentCodes.length === 0;

                  return (
                    <TextField
                      {...field}
                      select
                      label="Číslo / kód dokladu"
                      fullWidth
                      disabled={isLoading || isEmpty}
                      error={!!errors.document?.documentCode}
                      helperText={
                        errors.document?.documentCode?.message ||
                        (isLoading
                          ? 'Načítám číselník dokladů…'
                          : isEmpty
                            ? 'Žádné kódy nejsou k dispozici'
                            : 'Vyberte kód')
                      }
                      value={field.value ?? ''}
                    >
                      {isLoading && <MenuItem disabled>Načítám…</MenuItem>}
                      {!isLoading && isEmpty && <MenuItem disabled>Žádné možnosti</MenuItem>}
                      {!isLoading &&
                        !isEmpty &&
                        documentCodes!.map((d) => (
                          <MenuItem key={d.code} value={d.code}>
                            {d.code} {d.note ? `— ${d.note}` : ''}
                          </MenuItem>
                        ))}
                    </TextField>
                  );
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.issueDate"
                rules={{ required: 'Vyberte datum vystavení' }}
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    label="Datum vystavení"
                    value={value as Date | null}
                    onChange={onChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.document?.issueDate,
                        helperText: errors.document?.issueDate?.message
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.issueDays"
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Splatnost"
                    fullWidth
                    onChange={(e) =>
                      field.onChange(
                        Number.isNaN(Number.parseInt(e.target.value)) ? 0 : Number.parseInt(e.target.value)
                      )
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Dní</InputAdornment>,
                      inputProps: { step: 1, min: 0 }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.paymentMethod"
                render={({ field }) => (
                  <TextField {...field} label="Způsob platby" select fullWidth>
                    <MenuItem value="cash">Hotově</MenuItem>
                    <MenuItem value="bank">Převodem</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" gap={2}>
                <Controller
                  control={control}
                  name="document.taxEnabled"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={!!field.value} onChange={(_, checked) => field.onChange(checked)} />}
                      label="Plátce DPH"
                    />
                  )}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.isPaidAmount"
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Uhrazena částka"
                    disabled
                    fullWidth
                    onChange={(e) =>
                      field.onChange(
                        Number.isNaN(Number.parseFloat(e.target.value)) ? 0 : Number.parseFloat(e.target.value)
                      )
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Kč</InputAdornment>,
                      inputProps: { step: 1, min: 0 }
                    }}
                    helperText="Automaticky vypočítáno z položek"
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.isPaidDate"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    label="Datum úhrady"
                    value={value as Date | null}
                    onChange={onChange}
                    slotProps={{
                      textField: {
                        fullWidth: true
                      }
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.note"
                render={({ field }) => (
                  <TextField {...field} label="Poznámka" placeholder="Poznámka" fullWidth multiline rows={2} />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="document.internalNote"
                render={({ field }) => <TextField {...field} label="Interní poznámka" fullWidth multiline rows={2} />}
              />
            </Grid>
          </Grid>

          {/* POLOŽKY – ŘÁDKY FAKTURY */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12 }} display="flex" alignItems="center" justifyContent="space-between">
              <strong>Položky</strong>
            </Grid>

            {fields.map((field, index) => (
              <Grid
                key={field.id}
                container
                spacing={2}
                width={1}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <Grid size={{ xs: 12, md: 5 }}>
                  <Controller
                    control={control}
                    name={`items.${index}.description`}
                    rules={{ required: 'Popis položky je povinný' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Popis položky"
                        fullWidth
                        error={!!errors.items?.[index]?.description}
                        helperText={errors.items?.[index]?.description?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 1 }}>
                  <Controller
                    control={control}
                    name={`items.${index}.quantity`}
                    rules={{
                      min: { value: 0, message: 'Minimálně 0 Kč' }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Množství"
                        fullWidth
                        InputProps={{ inputProps: { step: 0.001, min: 0 } }}
                        error={!!errors.items?.[index]?.quantity}
                        helperText={errors.items?.[index]?.quantity?.message}
                        onChange={(e) => {
                          const value = Number.isNaN(Number.parseFloat(e.target.value))
                            ? 0
                            : Number.parseFloat(e.target.value);
                          field.onChange(value);
                          updateTotalAmount();
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                  <Controller
                    control={control}
                    name={`items.${index}.unit`}
                    defaultValue="ks"
                    render={({ field }) => <TextField {...field} label="Jednotka" fullWidth />}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 2 }}>
                  <Controller
                    control={control}
                    name={`items.${index}.unitPrice`}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Jednotková cena"
                        fullWidth
                        onChange={(e) => {
                          const value = Number.isNaN(Number.parseFloat(e.target.value))
                            ? 0
                            : Number.parseFloat(e.target.value);
                          field.onChange(value);
                          updateTotalAmount();
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">Kč</InputAdornment>,
                          inputProps: { step: 0.01, min: 0 }
                        }}
                      />
                    )}
                  />
                </Grid>

                {taxEnabled && (
                  <Grid size={{ xs: 12, md: 1 }}>
                    <Controller
                      control={control}
                      name={`items.${index}.tax`}
                      render={({ field }) => (
                        <TextField {...field} select label="DPH %" fullWidth>
                          <MenuItem value={12}>12 %</MenuItem>
                          <MenuItem value={21}>21 %</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                )}

                <Grid
                  size={{ xs: 12, md: 1 }}
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  <IconButton aria-label="Smazat položku" onClick={() => remove(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Grid size={12}>
              <Button startIcon={<AddIcon color="error" />} variant="outlined" onClick={addItem}>
                Přidat položku
              </Button>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="text">
            Zrušit
          </Button>
          <Button type="submit" variant="contained" size="large" loading={isLoading}>
            {defaultData ? 'Uložit změny' : 'Vytvořit dokument'}
          </Button>
        </DialogActions>
      </form>
    </BasicDialog>
  );
};
